import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getGsmDeviceDetails, getWeatherStationDeviceDetails } from "../services/devices";
import Table from "../components/tables/Table";
import { formatDateTime } from "../utils/FormatDateTime";
import Pagination from "../components/tables/pagination/Pagination";
import { GaugeComponent } from "react-gauge-component";

function DeviceDetailsPop({ item }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);

    const pageSize = 10;

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const fetchDevices = async () => {
        try {
            let res;
            if (item.device_type === "GSM Motor Controller - IOT" || item.device_type === "GSM Motor Controller - V2" || item.device_type === "GSM Motor Controller" || item.device_type === "GSM-V3") {
                res = await getGsmDeviceDetails(item.device_id);
            } else if (item.device_type === "Weather Station") {
                res = await getWeatherStationDeviceDetails(item.device_id);
            }
            if (res.status === 200) {
                setData(res.data);
            } else {
                toast.error("Unable to fetch device details");
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Error fetching device details");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDevices();
    }, []);

    if (loading) {
        return (
            <div className="p-4 bg-gray-50 min-h-screen max-w-7xl mx-auto flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-4 bg-gray-50 min-h-screen max-w-7xl mx-auto flex items-center justify-center">
                <p className="text-red-500">Failed to load device details</p>
            </div>
        );
    }

    const renderDeviceSpecificDetails = () => {
        switch (item.device_type) {
            case "GSM Motor Controller - IOT":
            case "GSM Motor Controller - V2":
            case "GSM Motor Controller":
            case "GSM-V3":
                return (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-white p-4 rounded-lg shadow">
                                <h3 className="font-bold text-lg mb-2">Current Status</h3>
                                <p>Motor: <span className={`font-semibold ${data.motor === "ON" ? "text-green-600" : "text-red-600"}`}>{data.motor}</span></p>
                                <p>Signal Strength: <span className="font-semibold">{data.signal}%</span></p>
                                <p>Internet: <span className="font-semibold">{data.inter_net}%</span></p>
                                <p>Current: <span className="font-semibold">{data.current}A</span></p>
                                <p>Phase: <span className="font-semibold">{data.phase}</span></p>
                            </div>

                            <div className="bg-white p-4 rounded-lg shadow">
                                <h3 className="font-bold text-lg mb-2">Device Information</h3>
                                <p>Device ID: <span className="font-semibold">{data.device_id}</span></p>
                                <p>Device Name: <span className="font-semibold">{data.device_name || "N/A"}</span></p>
                                <p>Owner Number: <span className="font-semibold">{data.owner_number}</span></p>
                                <p>Subscription: <span className="font-semibold">{new Date(data.subscription_start_date).toLocaleDateString()} - {new Date(data.subscription_end_date).toLocaleDateString()}</span></p>
                                <p>Status: <span className={`font-semibold ${data.is_active ? "text-green-600" : "text-red-600"}`}>{data.is_active ? "Active" : "Inactive"}</span></p>
                            </div>
                        </div>

                        <div className="bg-white p-4 rounded-lg shadow mt-4">
                            <h3 className="font-bold text-lg mb-2">Settings</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <p>Auto Mode: <span className={`font-semibold ${data.auto === "ON" ? "text-green-600" : "text-red-600"}`}>{data.auto}</span></p>
                                    <p>Dry Run Protection: <span className={`font-semibold ${data.dry_run === "ON" ? "text-green-600" : "text-red-600"}`}>{data.dry_run}</span></p>
                                </div>
                                <div>
                                    <p>Current Upper Limit: <span className="font-semibold">{data.current_upper_val}A</span></p>
                                    <p>Current Lower Limit: <span className="font-semibold">{data.current_lower_val}A</span></p>
                                </div>
                                <div>
                                    <p>Timer: <span className="font-semibold">{data.timer}</span></p>
                                    <p>Alert Type: <span className="font-semibold">{data.alert_type}</span></p>
                                </div>
                            </div>
                        </div>

                        {data.uptime_history?.length > 0 && (
                            <div className="bg-white p-4 rounded-lg shadow mt-4">
                                <h3 className="font-bold text-lg mb-2">Uptime History</h3>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Power (kWh)</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consumption (kWh)</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {data.uptime_history.map((history, index) => (
                                                <tr key={index}>
                                                    <td className="px-6 py-4 whitespace-nowrap">{history.date}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap">{history.power}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap">{history.consumption}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {data.scheduler_data?.length > 0 && (
                            <div className="bg-white p-4 rounded-lg shadow mt-4">
                                <h3 className="font-bold text-lg mb-2">Schedules</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {data.scheduler_data.map((schedule, index) => (
                                        <div key={schedule.id} className="border rounded p-3">
                                            <p className="font-semibold">Schedule {index + 1}</p>
                                            <p>Start: {schedule.start_time}</p>
                                            <p>End: {schedule.end_time}</p>
                                            <p>Status: <span className={`font-semibold ${schedule.is_active ? "text-green-600" : "text-red-600"}`}>{schedule.is_active ? "Active" : "Inactive"}</span></p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                );

            case "Soil Test":
                return (
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="font-bold text-lg mb-2">Soil Test Data</h3>
                        <p>Device specific soil test data will be displayed here.</p>
                        {/* Add specific fields for Soil Test when you have the data structure */}
                    </div>
                );

            case "Weather Station":
                const weatherData = data?.results?.[0];
                const allResults = data?.results || [];
                const sensors = weatherData?.sensors || [];

                // Prepare chart data from recent readings (last 10 readings)
                const chartData = allResults.slice(0, 10).reverse().map(reading => ({
                    time: new Date(reading.created_at).toLocaleTimeString(),
                    timestamp: reading.created_at,
                    ...reading.sensors.reduce((acc, sensor) => {
                        acc[sensor.sensor_name] = parseFloat(sensor.value) || 0;
                        return acc;
                    }, {})
                }));



                return (
                    <div className="p-6 rounded-xl shadow-xl border">
                        {/* Header with Weather Icon */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center space-x-3">
                                <div className="bg-blue-500 p-3 rounded-full shadow-lg">
                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-2xl font-bold text-gray-800">Weather Station</h3>
                                    <p className="text-sm text-gray-600">Real-time environmental monitoring</p>
                                </div>
                            </div>
                            {weatherData?.created_at && (
                                <div className="text-right">
                                    <div className="text-sm text-gray-500">Last Updated</div>
                                    <div className="text-sm font-medium text-gray-700">
                                        {new Date(weatherData.created_at).toLocaleString()}
                                    </div>
                                </div>
                            )}
                        </div>

                        {sensors.length > 0 ? (
                            <div className="space-y-6">
                                {/* Key Metrics Dashboard */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    {sensors && sensors.length > 0 ? sensors.slice(0, 4).map((sensor, index) => {
                                        const getCardStyle = (sensorName) => {
                                            if (sensorName.includes('Temperature')) return 'border-blue-200 text-blue-600';
                                            if (sensorName.includes('Humidity')) return 'border-green-200 text-green-600';
                                            if (sensorName.includes('Wind')) return 'border-purple-200 text-purple-600';
                                            if (sensorName.includes('Battery')) return 'border-red-200 text-red-600';
                                            if (sensorName.includes('UV')) return 'border-orange-200 text-orange-600';
                                            return 'border-gray-200 text-gray-600';
                                        };

                                        const getIcon = (sensorName) => {
                                            const icons = {
                                                'Temperature': 'M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z',
                                                'Humidity': 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
                                                'Wind': 'M13 10V3L4 14h7v7l9-11h-7z',
                                                'Battery': 'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6',
                                                'UV': 'M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z',
                                                'default': 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
                                            };

                                            const iconKey = Object.keys(icons).find(key => sensorName.includes(key)) || 'default';
                                            return (
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icons[iconKey]} />
                                                </svg>
                                            );
                                        };

                                        const getUnit = (sensorName) => {
                                            const units = {
                                                'Temperature': '°C',
                                                'Humidity': '%',
                                                'Wind Speed': ' m/s',
                                                'Rainfall': ' mm',
                                                'UV': ' W/m²',
                                                'Battery': 'V',
                                                'Wind Direction': ''
                                            };
                                            return Object.entries(units).find(([key]) => sensorName.includes(key))?.[1] || '';
                                        };

                                        const avgValue = chartData.length > 0 ? chartData.reduce((sum, d) => sum + (parseFloat(d[sensor.sensor_name]) || 0), 0) / chartData.length : 0;

                                        // Sensor configurations for gauges
                                        const sensorConfigs = {
                                            'Temperature': {
                                                value: parseFloat(sensor.value) || 0,
                                                min: -10, max: 40,
                                                subArcs: [
                                                    { limit: 0, color: "#5BE12C" },
                                                    { limit: 10, color: "#F5CD19" },
                                                    { limit: 20, color: "#F58019" },
                                                    { limit: 40, color: "#EA5AAA" }
                                                ],
                                                formatValue: (v) => `${v}°C`,
                                                borderColor: 'border-blue-200',
                                                avgFormat: (v) => `${v.toFixed(1)}°C`
                                            },
                                            'Humidity': {
                                                value: parseFloat(sensor.value) || 0,
                                                min: 0, max: 100,
                                                subArcs: [
                                                    { limit: 20, color: "#EA5AAA" },
                                                    { limit: 40, color: "#F58019" },
                                                    { limit: 60, color: "#F5CD19" },
                                                    { limit: 80, color: "#5BE12C" },
                                                    { limit: 100, color: "#00D4FF" }
                                                ],
                                                formatValue: (v) => `${v}%`,
                                                borderColor: 'border-green-200',
                                                avgFormat: (v) => `${v.toFixed(1)}%`
                                            },
                                            'UV': {
                                                value: parseFloat(sensor.value) || 0,
                                                min: 0, max: 15,
                                                subArcs: [
                                                    { limit: 3, color: "#5BE12C" },
                                                    { limit: 6, color: "#F5CD19" },
                                                    { limit: 8, color: "#F58019" },
                                                    { limit: 11, color: "#EA5AAA" },
                                                    { limit: 15, color: "#8B0000" }
                                                ],
                                                formatValue: (v) => `${v} W/m²`,
                                                borderColor: 'border-orange-200',
                                                avgFormat: (v) => `${v.toFixed(1)} W/m²`
                                            },
                                            'Battery': {
                                                value: parseFloat(sensor.value) || 0,
                                                min: 2.5, max: 5.0,
                                                subArcs: [
                                                    { limit: 3.0, color: "#EA5AAA" },
                                                    { limit: 3.5, color: "#F58019" },
                                                    { limit: 4.0, color: "#F5CD19" },
                                                    { limit: 4.5, color: "#5BE12C" },
                                                    { limit: 5.0, color: "#00D4FF" }
                                                ],
                                                formatValue: (v) => `${v}V`,
                                                borderColor: 'border-red-200',
                                                avgFormat: (v) => `${v.toFixed(2)}V`
                                            },
                                            'Wind Speed': {
                                                value: parseFloat(sensor.value) || 0,
                                                min: 0, max: 30,
                                                subArcs: [
                                                    { limit: 5, color: "#5BE12C" },
                                                    { limit: 10, color: "#F5CD19" },
                                                    { limit: 15, color: "#F58019" },
                                                    { limit: 20, color: "#EA5AAA" },
                                                    { limit: 30, color: "#8B0000" }
                                                ],
                                                formatValue: (v) => `${v} m/s`,
                                                borderColor: 'border-purple-200',
                                                avgFormat: (v) => `${v.toFixed(1)} m/s`
                                            }
                                        };

                                        // Find sensor type and render appropriate component
                                        const sensorType = Object.keys(sensorConfigs).find(type => sensor.sensor_name.includes(type));

                                        if (sensorType) {
                                            const config = sensorConfigs[sensorType];
                                            return (
                                                <div key={index} className={`bg-slate-300 rounded-xl p-4 shadow-lg border ${config.borderColor}`}>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                                                            {getIcon(sensor.sensor_name)}
                                                            <span className="ml-2">{sensor.sensor_name}</span>
                                                        </h4>
                                                        <div className="text-xs text-gray-500">Avg: {config.avgFormat(avgValue)}</div>
                                                    </div>
                                                    <div className="relative h-32 flex items-center justify-center">
                                                        <GaugeComponent
                                                            value={config.value}
                                                            type="semicircle"
                                                            arc={{
                                                                width: 0.2,
                                                                padding: 0.005,
                                                                cornerRadius: 1,
                                                                subArcs: config.subArcs,
                                                            }}
                                                            pointer={{
                                                                color: "#345243",
                                                                length: 0.80,
                                                                width: 15,
                                                            }}
                                                            labels={{
                                                                valueLabel: {
                                                                    formatTextValue: config.formatValue,
                                                                },
                                                            }}
                                                            minValue={config.min}
                                                            maxValue={config.max}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        } else if (sensor.sensor_name.includes('Wind Direction')) {
                                            // Compass for wind direction
                                            const windDirection = parseFloat(sensor.value) || 0;

                                            return (
                                                <div key={index} className="bg-slate-300 rounded-xl p-4 shadow-lg border border-purple-200">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                                                            {getIcon(sensor.sensor_name)}
                                                            <span className="ml-2">{sensor.sensor_name}</span>
                                                        </h4>
                                                        <div className="text-xs text-gray-500">Current</div>
                                                    </div>
                                                    <div className="relative h-32 flex items-center justify-center">
                                                        <div className="relative w-24 h-24">
                                                            <div className="w-24 h-24 rounded-full border-4 border-gray-300 bg-white relative">
                                                                {['N', 'S', 'W', 'E'].map((dir, i) => (
                                                                    <div key={dir} className="absolute inset-0 flex items-center justify-center">
                                                                        <div className={`text-xs font-bold text-gray-600 ${dir === 'N' ? '' :
                                                                            dir === 'S' ? 'transform translate-y-8' :
                                                                                dir === 'W' ? 'transform -translate-x-8' :
                                                                                    'transform translate-x-8'
                                                                            }`}>{dir}</div>
                                                                    </div>
                                                                ))}
                                                                <div
                                                                    className="absolute top-1/2 left-1/2 w-1 h-8 bg-red-500 transform -translate-x-1/2 -translate-y-1/2 origin-bottom"
                                                                    style={{ transform: `translate(-50%, -50%) rotate(${windDirection}deg)` }}
                                                                ></div>
                                                                <div className="absolute top-1/2 left-1/2 w-2 h-2 bg-red-500 rounded-full transform -translate-x-1/2 -translate-y-1/2"></div>
                                                            </div>
                                                        </div>
                                                        <div className="absolute -bottom-2 text-center">
                                                            <div className="text-lg font-bold text-purple-600">{windDirection}°</div>
                                                            <div className="text-xs text-gray-500">Direction</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        } else {
                                            // Progress indicator for other sensors
                                            const gaugeValue = Math.max(0, Math.min(100, parseFloat(sensor.value) || 0));
                                            const cardStyle = getCardStyle(sensor.sensor_name);
                                            const colorClass = cardStyle.split(' ')[1];

                                            return (
                                                <div key={index} className={`bg-slate-300 rounded-xl p-4 shadow-lg border ${cardStyle}`}>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h4 className="text-sm font-semibold text-gray-700 flex items-center">
                                                            {getIcon(sensor.sensor_name)}
                                                            <span className="ml-2">{sensor.sensor_name}</span>
                                                        </h4>
                                                        <div className="text-xs text-gray-500">Avg: {avgValue.toFixed(1)}{getUnit(sensor.sensor_name)}</div>
                                                    </div>
                                                    <div className="relative h-24 flex items-center justify-center">
                                                        <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                                                            <path className="text-gray-200" strokeWidth="3" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                                            <path className={colorClass} strokeWidth="3" strokeDasharray={`${gaugeValue}, 100`} strokeDashoffset="25" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                                        </svg>
                                                        <div className="absolute text-center">
                                                            <div className={`text-2xl font-bold ${colorClass}`}>
                                                                {sensor.value}{getUnit(sensor.sensor_name)}
                                                            </div>
                                                            <div className="text-xs text-gray-500">Current</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        }
                                    }) : (
                                        <div className="col-span-full text-center py-8">
                                            <div className="text-gray-500">No sensor data available</div>
                                        </div>
                                    )}
                                </div>

                                {/* All Sensors Grid */}
                                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                                    <h4 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        All Sensor Readings
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                                        {sensors.map((sensor, index) => {
                                            const getCardStyle = (sensorName) => {
                                                if (sensorName.includes('Temperature')) return 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300 text-blue-800';
                                                if (sensorName.includes('Humidity')) return 'bg-gradient-to-br from-green-50 to-green-100 border-green-300 text-green-800';
                                                if (sensorName.includes('Wind')) return 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-300 text-purple-800';
                                                if (sensorName.includes('Battery')) return 'bg-gradient-to-br from-red-50 to-red-100 border-red-300 text-red-800';
                                                return 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300 text-orange-800';
                                            };

                                            const getUnit = (sensorName) => {
                                                if (sensorName.includes('Temperature')) return '°C';
                                                if (sensorName.includes('Humidity')) return '%';
                                                if (sensorName.includes('Wind Speed')) return ' m/s';
                                                if (sensorName.includes('Rainfall')) return ' mm';
                                                if (sensorName.includes('UV')) return ' W/m²';
                                                if (sensorName.includes('Battery')) return 'V';
                                                if (sensorName.includes('Wind Direction')) return '';
                                                return '%';
                                            };

                                            return (
                                                <div key={index} className={`p-4 rounded-lg border-2 ${getCardStyle(sensor.sensor_name)} shadow-sm hover:shadow-md transition-shadow`}>
                                                    <div className="text-xs font-medium mb-2 opacity-75">{sensor.sensor_name}</div>
                                                    <div className="text-lg font-bold">
                                                        {sensor.value}{getUnit(sensor.sensor_name)}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Enhanced Data Table */}
                                <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                                    <h4 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                        </svg>
                                        Historical Data
                                    </h4>
                                    {allResults && allResults.length > 0 && (
                                        <Table
                                            headers={[
                                                {
                                                    label: "Time",
                                                    key: "created_at",
                                                },
                                                ...allResults[0].sensors.map(sensor => ({
                                                    label: sensor.sensor_name,
                                                    key: sensor.sensor_name,
                                                }))
                                            ]}
                                            rows={allResults?.map((item, index) => {
                                                const rowData = {
                                                    created_at: <p>{formatDateTime(item.created_at)}</p>,
                                                };

                                                // Dynamically add sensor data
                                                item.sensors.forEach(sensor => {
                                                    rowData[sensor.sensor_name] = <p>{sensor.value}</p>;
                                                });

                                                return rowData;
                                            })}
                                        />
                                    )}
                                    {
                                        data.count !== 0 ?
                                            <Pagination
                                                currentPage={currentPage}
                                                pageSize={pageSize}
                                                totalEntries={data?.count}
                                                onPageChange={handlePageChange}
                                                data={data}
                                                setData={setData}
                                            />
                                            : <></>
                                    }
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12">
                                <div className="text-gray-500 text-lg">No weather data available</div>
                                <div className="text-gray-400 text-sm mt-2">Please check device connection</div>
                            </div>
                        )}
                    </div>
                );

            case "Data Logger":
                return (
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="font-bold text-lg mb-2">Data Logger</h3>
                        <p>Device specific data logger information will be displayed here.</p>
                        {/* Add specific fields for Data Logger when you have the data structure */}
                    </div>
                );

            default:
                return (
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="font-bold text-lg mb-2">Device Information</h3>
                        <pre>{JSON.stringify(data, null, 2)}</pre>
                    </div>
                );
        }
    };

    return (
        <div className="p-4 space-y-4 bg-gray-50 min-h-screen max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold">{item.device_type}</h2>
                    <p className="text-gray-600">Device ID: {item.device_id}</p>
                </div>
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-white shadow">
                    <img
                        src={item.device_type_image}
                        alt={item.device_type}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                            e.target.src = "https://via.placeholder.com/150";
                        }}
                    />
                </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="font-bold text-lg mb-2">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                        <p>User: <span className="font-semibold">{item.user}</span></p>
                        <p>Phone: <span className="font-semibold">{item.phone}</span></p>
                    </div>
                    <div>
                        <p>State: <span className="font-semibold">{item.state}</span></p>
                        <p>Date: <span className="font-semibold">{item.date}</span></p>
                    </div>
                    <div>
                        <p>Subscription Start: <span className="font-semibold">{new Date(item.subscription_start_date).toLocaleDateString()}</span></p>
                        <p>Subscription End: <span className="font-semibold">{new Date(item.subscription_end_date).toLocaleDateString()}</span></p>
                    </div>
                </div>
            </div>

            {renderDeviceSpecificDetails()}
        </div>
    );
}

export default DeviceDetailsPop;